import graphql from "babel-plugin-relay/macro";
import { atom, useAtomValue } from "jotai";
import { ReactNode, useMemo } from "react";
import { useFragment } from "react-relay";

import type { useSignInMutation$data } from "@/features/Authentication/hooks/__generated__/useSignInMutation.graphql";
import { AnimatedStack } from "@whoosh/design-system/src/components/animations/AnimatedStack";

import { SessionClub } from "../../../../../../Session";
import { VerificationModalStepStateAtom } from "../../atoms/stores";
import { VerifiedAuthenticationModalBodyFragment_session$key } from "./__generated__/VerifiedAuthenticationModalBodyFragment_session.graphql";
import { AccessYourAccountStep } from "./components/AccessourAccountStep/AccessYourAccountStep";
import { AccountCreationStep } from "./components/AccountCreationStep/AccountCreationStep";
import { AccountNotFoundStep } from "./components/AccountNotFoundStep/AccountNotFoundStep";
import { ActivateYourAccountStep } from "./components/ActivateYourAccountStep/ActivateYourAccountStep";
import { ClubSelectionStep } from "./components/ClubSelectionStep/ClubSelectionStep";
import { EnterPasswordStep } from "./components/EnterPasswordStep/EnterPasswordStep";
import { InitialStep } from "./components/InitialStep/InitialStep";
import { JoinNewClubStep } from "./components/JoinNewClubStep/JoinNewClubStep";
import { RequestRecievedStep } from "./components/RequestReceivedStep/RequestReceivedStep";
import { ResetPasswordStep } from "./components/ResetPasswordStep/ResetPasswordStep";
import { SetPasswordStep } from "./components/SetPasswordStep/SetPasswordStep";
import { VerifyCodeStep } from "./components/VerifyCodeStep/VerifyCodeStep";
import { WelcomeStep } from "./components/WelcomeStep/WelcomeStep";

export const VerifiedAuthenticationModalBody = ({
  sessionRef,
  hasAuthenticatedUser,
  clubSlug,
  sessionClub,
  onComplete,
  onResetPasswordLoginCompleted,
  alternativeSignIn,
}: {
  sessionRef: VerifiedAuthenticationModalBodyFragment_session$key | null;
  hasAuthenticatedUser: boolean;
  clubSlug?: string;
  sessionClub: SessionClub | null;
  onComplete: (clubSlug?: string) => void;
  onResetPasswordLoginCompleted: (data: useSignInMutation$data) => void;
  alternativeSignIn?: ReactNode;
}) => {
  const session = useFragment(
    VerifiedAuthenticationModalBodyFragmentString,
    sessionRef
  );

  const activeStackKeys = useAtomValue(CurrentStackKeyListAtom);

  const stackItems = useMemo(
    () => [
      {
        key: "Initial",
        render: () => (
          <InitialStep
            sessionRef={session ?? null}
            clubSlug={clubSlug}
            sessionClub={sessionClub}
            alternativeSignIn={alternativeSignIn}
          />
        ),
      },
      {
        key: "VerifyCode",
        render: () => (
          <VerifyCodeStep sessionRef={session ?? null} clubSlug={clubSlug} />
        ),
      },
      {
        key: "AccountCreation",
        render: () => (
          <AccountCreationStep
            sessionRef={session ?? null}
            hasAuthenticatedUser={hasAuthenticatedUser}
          />
        ),
      },
      {
        key: "EnterPassword",
        render: () => (
          <EnterPasswordStep
            onComplete={onComplete}
            alternativeSignIn={alternativeSignIn}
          />
        ),
      },
      { key: "AccountNotFound", render: () => <AccountNotFoundStep /> },
      {
        key: "ClubSelection",
        render: () => <ClubSelectionStep />,
      },
      {
        key: "Welcome",
        render: () => <WelcomeStep />,
      },
      { key: "AccessYourAccount", render: () => <AccessYourAccountStep /> },
      { key: "ActivateAccount", render: () => <ActivateYourAccountStep /> },
      { key: "RequestRecieved", render: () => <RequestRecievedStep /> },
      {
        key: "SetPassword",
        render: () => <SetPasswordStep clubSlug={clubSlug} />,
      },

      {
        key: "JoinNewClub",
        render: () => <JoinNewClubStep sessionRef={session ?? null} />,
      },
      {
        key: "ResetPassword",
        render: () => (
          <ResetPasswordStep
            clubSlug={clubSlug}
            onLoginCompleted={onResetPasswordLoginCompleted}
          />
        ),
      },
    ],
    [
      session,
      hasAuthenticatedUser,
      clubSlug,
      sessionClub,
      onComplete,
      onResetPasswordLoginCompleted,
      alternativeSignIn,
    ]
  );

  return <AnimatedStack activeItemKeys={activeStackKeys} items={stackItems} />;
};

const CurrentStackKeyListAtom = atom((get) => {
  const stepState = get(VerificationModalStepStateAtom);
  return stepState.backStep
    ? [stepState.backStep, stepState.step]
    : [stepState.step];
});

export const VerifiedAuthenticationModalBodyFragmentString = graphql`
  fragment VerifiedAuthenticationModalBodyFragment_session on Session {
    ...AccountCreationStepFragment_session
    ...JoinNewClubStepFragment_session
    ...VerifyCodeStep_session
    ...InitialStepAuthenticationModalFragment_Session
  }
`;
